import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import {
  CreditCard, ArrowLeft, ShieldCheck, CheckCircle2, AlertTriangle, Search, User, X, Loader2,
  BanknoteIcon, Smartphone, Wallet, Download, Mail, MessageSquare, Radio,
} from 'lucide-react';
import { toast } from 'sonner';
import { StripeCardForm } from './stripe/StripeCardForm';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const outstandingOf = (b) => Number(b?.balance_due ?? b?.outstanding ?? 0) || 0;

/**
 * PaymentModal — unified Stripe Elements payment modal + standalone walk-in POS.
 *
 * Booking / customer mode (posMode=false): details → card → success.
 * Walk-in POS mode (posMode=true, admin): pick/enter a customer → amount +
 * receipt controls → choose a method (Card / Cash / PayPal / E-Transfer /
 * tap reader) → success (with PDF receipt download + sent status).
 */
export const PaymentModal = ({
  open, onOpenChange, booking = null, bookings = [], mode = 'customer',
  defaultAmount = null, title, onSuccess, paymentType = null,
  posMode = false, presetCustomer = null,
}) => {
  const isAdmin = mode === 'admin';
  const bookingList = useMemo(
    () => (booking ? [booking] : (bookings || [])),
    [booking, bookings],
  );

  const [selectedBooking, setSelectedBooking] = useState(null);
  const [step, setStep] = useState('details'); // booking|details|method|card|cash|paypal|etransfer|terminal|success
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [pin, setPin] = useState('');
  const [search, setSearch] = useState('');
  const [success, setSuccess] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cashReceived, setCashReceived] = useState('');
  const [addHst, setAddHst] = useState(false);

  // POS customer linkage (posMode only)
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [custSearch, setCustSearch] = useState('');
  const [custResults, setCustResults] = useState([]);
  const [custSearching, setCustSearching] = useState(false);
  const [oneTimeActive, setOneTimeActive] = useState(false);
  const [oneTimeForm, setOneTimeForm] = useState({ name: '', email: '', phone: '' });
  const [oneTimeFlag, setOneTimeFlag] = useState(true); // "This is a one-time customer"
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(true);

  // Method capability flags (posMode)
  const [stripeConfig, setStripeConfig] = useState(null);
  const [paypalConfig, setPaypalConfig] = useState(null);

  // Terminal sub-state
  const [terminal, setTerminal] = useState(null);
  const [reader, setReader] = useState(null);
  const [cardStatus, setCardStatus] = useState(null);

  const initialisedRef = useRef(false);
  useEffect(() => {
    if (!open) { initialisedRef.current = false; return; }
    if (initialisedRef.current) return;
    initialisedRef.current = true;

    const initial = bookingList.length === 1 ? bookingList[0] : null;
    setSelectedBooking(initial);
    setStep(posMode || bookingList.length === 1 || (bookingList.length === 0 && isAdmin) ? 'details' : 'booking');
    setAmount(
      defaultAmount != null
        ? String(Number(defaultAmount).toFixed(2))
        : (initial ? String(outstandingOf(initial).toFixed(2)) : ''),
    );
    setNote('');
    setPin('');
    setSearch('');
    setCashReceived('');
    setAddHst(false);
    setSuccess(null);
    setBusy(false);
    setSelectedCustomer(posMode ? (presetCustomer || null) : null);
    setCustSearch(''); setCustResults([]); setCustSearching(false);
    setOneTimeActive(false); setOneTimeForm({ name: '', email: '', phone: '' });
    setOneTimeFlag(true); setSendEmail(true); setSendSms(true);
    setCardStatus(null); setTerminal(null); setReader(null);
    if (posMode) {
      Promise.all([
        axios.get(`${API_URL}/api/admin/stripe-terminal/config`, { withCredentials: true }).catch(() => ({ data: { configured: false } })),
        axios.get(`${API_URL}/api/payments/paypal/client-id`).catch(() => ({ data: { configured: false } })),
      ]).then(([s, p]) => { setStripeConfig(s.data); setPaypalConfig(p.data); });
    }
  }, [open, bookingList, defaultAmount, isAdmin, posMode, presetCustomer]);

  // Debounced customer search (POS mode only)
  useEffect(() => {
    if (!open || !posMode) return;
    const q = custSearch.trim();
    if (q.length < 2) { setCustResults([]); return; }
    let cancelled = false;
    setCustSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API_URL}/api/admin/customers`, { params: { search: q }, withCredentials: true });
        if (!cancelled) setCustResults((r.data || []).slice(0, 8));
      } catch { if (!cancelled) setCustResults([]); }
      finally { if (!cancelled) setCustSearching(false); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [custSearch, open, posMode]);

  const close = () => {
    if (terminal && reader) { try { terminal.disconnectReader?.(); } catch { /* noop */ } }
    onOpenChange(false);
  };

  const pickBooking = (b) => {
    setSelectedBooking(b);
    setAmount(String(outstandingOf(b).toFixed(2)));
    setStep('details');
  };

  const outstanding = outstandingOf(selectedBooking);
  const amtNum = Number(amount);
  // HST (iter-193) — OFF by default for manual/POS sales. When ON, 13% is added
  // ON TOP of the entered (pre-tax) amount. POS-only (booking payments keep the
  // booking's own HST). `totalCharge` is what the customer actually pays.
  const hstAmount = addHst ? Math.round((Number(amount) || 0) * 0.13 * 100) / 100 : 0;
  const totalCharge = Math.round(((Number(amount) || 0) + hstAmount) * 100) / 100;
  const amountLocked = paymentType === 'deposit' || paymentType === 'balance';
  const amountValid = amtNum > 0;
  const overpay = isAdmin && !amountLocked && selectedBooking && amtNum > outstanding + 0.005;
  const pinValid = !isAdmin || pin.length === 4;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return bookingList;
    return bookingList.filter((b) =>
      [b.booking_id, b.child_name, b.customer_name, b.customer_email, b.customer_phone, b.date]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [bookingList, search]);

  // Effective customer for the standalone POS charge (selected OR one-time OR none)
  const effCustomer = useMemo(() => {
    if (selectedCustomer) {
      return {
        email: selectedCustomer.email || null,
        name: selectedCustomer.name || null,
        phone: selectedCustomer.phone || null,
        customer_id: selectedCustomer.customer_id || null,
      };
    }
    if (oneTimeActive && oneTimeForm.name.trim()) {
      return {
        email: (oneTimeForm.email || '').trim() || null,
        name: oneTimeForm.name.trim(),
        phone: (oneTimeForm.phone || '').trim() || null,
        customer_id: null,
      };
    }
    return null;
  }, [selectedCustomer, oneTimeActive, oneTimeForm]);

  // One-time only applies to a brand-new (one-time-form) customer, never an
  // already-selected real customer.
  const effectiveOneTime = oneTimeActive ? oneTimeFlag : false;
  const canEmailReceipt = !!effCustomer?.email;
  const canSmsReceipt = !!effCustomer?.phone;

  const commonPosPayload = () => ({
    customer_email: effCustomer?.email || null,
    customer_name: effCustomer?.name || null,
    customer_phone: effCustomer?.phone || null,
    customer_id: effCustomer?.customer_id || null,
    is_one_time: effectiveOneTime,
    send_email: sendEmail && canEmailReceipt,
    send_sms: sendSms && canSmsReceipt,
    note: note || null,
  });

  const finishPos = (data) => {
    setSuccess(data || {});
    setStep('success');
    onSuccess?.(data);
  };

  // ── Customer picker (POS) ─────────────────────────────────────────────────
  const renderCustomerPicker = () => (
    <div className="space-y-2" data-testid="pm-customer-section">
      <Label className="text-xs flex items-center gap-1"><User className="w-3 h-3" /> Customer <span className="text-[#6B6B7A] font-normal">(optional)</span></Label>
      {selectedCustomer ? (
        <div className="flex items-center justify-between bg-[#FFFDF8] border border-[#E8E5DF] rounded-xl p-3" data-testid="pm-customer-selected">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[#1E1E24] truncate">{selectedCustomer.name || selectedCustomer.email}</p>
            <p className="text-xs text-[#6B6B7A] truncate">{selectedCustomer.email}{selectedCustomer.phone ? ` · ${selectedCustomer.phone}` : ''}</p>
          </div>
          <Button variant="ghost" size="sm" className="text-xs flex-shrink-0" onClick={() => { setSelectedCustomer(null); setCustSearch(''); }} data-testid="pm-customer-change"><X className="w-3 h-3 mr-1" /> Change</Button>
        </div>
      ) : oneTimeActive ? (
        <div className="space-y-2 bg-[#FFFDF8] border border-[#E8E5DF] rounded-xl p-3" data-testid="pm-onetime-form">
          <Input value={oneTimeForm.name} onChange={(e) => setOneTimeForm((f) => ({ ...f, name: e.target.value }))} placeholder="Full name" className="rounded-xl border-[#E8E5DF] h-9 text-sm" data-testid="pm-onetime-name" />
          <Input value={oneTimeForm.email} onChange={(e) => setOneTimeForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email (for receipt)" className="rounded-xl border-[#E8E5DF] h-9 text-sm" data-testid="pm-onetime-email" />
          <Input value={oneTimeForm.phone} onChange={(e) => setOneTimeForm((f) => ({ ...f, phone: e.target.value }))} placeholder="Phone (for SMS receipt)" className="rounded-xl border-[#E8E5DF] h-9 text-sm" data-testid="pm-onetime-phone" />
          <div className="flex items-center justify-between bg-white border border-[#E8E5DF] rounded-lg px-3 py-2">
            <div>
              <p className="text-xs font-semibold text-[#1E1E24]">One-time customer</p>
              <p className="text-[10px] text-[#6B6B7A]">Badges them in the CRM & excludes from marketing</p>
            </div>
            <Switch checked={oneTimeFlag} onCheckedChange={setOneTimeFlag} data-testid="pm-onetime-toggle" />
          </div>
          <button type="button" onClick={() => { setOneTimeActive(false); setOneTimeForm({ name: '', email: '', phone: '' }); }} className="text-xs text-[#6B6B7A] hover:text-[#FF3B6B]" data-testid="pm-onetime-cancel">Cancel</button>
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="pm-customer-search-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B7A]" />
            <Input value={custSearch} onChange={(e) => setCustSearch(e.target.value)} placeholder="Search customers by name, email or phone…" className="rounded-xl border-[#E8E5DF] pl-9 h-9 text-sm" data-testid="pm-customer-search" />
          </div>
          {custSearch.trim().length >= 2 && (
            <div className="max-h-[28vh] overflow-y-auto space-y-1 border border-[#E8E5DF] rounded-xl p-1" data-testid="pm-customer-results">
              {custSearching && <p className="text-xs text-[#6B6B7A] p-2 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Searching…</p>}
              {!custSearching && custResults.length === 0 && <p className="text-xs text-[#6B6B7A] p-2" data-testid="pm-customer-empty">No matches. Add a one-time customer below.</p>}
              {custResults.map((c) => (
                <button key={c.email} type="button" onClick={() => { setSelectedCustomer(c); setCustSearch(''); setCustResults([]); }} className="w-full text-left rounded-lg p-2 hover:bg-[#FFFDF8]" data-testid={`pm-customer-pick-${c.email}`}>
                  <p className="text-sm font-medium text-[#1E1E24] truncate">{c.name || c.email}</p>
                  <p className="text-[11px] text-[#6B6B7A] truncate">{c.email}{c.phone ? ` · ${c.phone}` : ''}{c.total_spent ? ` · $${Number(c.total_spent).toFixed(0)} spent` : ''}</p>
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-3 pt-0.5">
            <button type="button" onClick={() => { setOneTimeActive(true); setOneTimeFlag(true); }} className="text-xs font-semibold text-[#FF3B6B] hover:underline" data-testid="pm-add-onetime">+ One-time customer</button>
            <span className="text-[#E8E5DF]">|</span>
            <span className="text-xs text-[#6B6B7A]" data-testid="pm-anon-hint">Leave blank for an anonymous charge</span>
          </div>
        </div>
      )}
    </div>
  );

  // ── Receipt controls (POS) ────────────────────────────────────────────────
  const renderReceiptControls = () => (
    <div className="space-y-2 bg-[#FFFDF8] border border-[#E8E5DF] rounded-xl p-3" data-testid="pm-receipt-controls">
      <p className="text-xs font-semibold text-[#1E1E24]">Send receipt</p>
      <label className={`flex items-center justify-between text-sm ${canEmailReceipt ? '' : 'opacity-50'}`}>
        <span className="flex items-center gap-2 text-[#6B6B7A]"><Mail className="w-3.5 h-3.5" /> Email {effCustomer?.email ? `to ${effCustomer.email}` : '(no email on file)'}</span>
        <Switch checked={sendEmail && canEmailReceipt} disabled={!canEmailReceipt} onCheckedChange={setSendEmail} data-testid="pm-send-email-toggle" />
      </label>
      <label className={`flex items-center justify-between text-sm ${canSmsReceipt ? '' : 'opacity-50'}`}>
        <span className="flex items-center gap-2 text-[#6B6B7A]"><MessageSquare className="w-3.5 h-3.5" /> Text {effCustomer?.phone ? `to ${effCustomer.phone}` : '(no phone on file)'}</span>
        <Switch checked={sendSms && canSmsReceipt} disabled={!canSmsReceipt} onCheckedChange={setSendSms} data-testid="pm-send-sms-toggle" />
      </label>
    </div>
  );

  // ── Step: pick a booking ────────────────────────────────────────────────
  const renderBookingPick = () => (
    <div className="space-y-2" data-testid="pm-step-booking">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B7A]" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by reference, name, email or phone…" className="rounded-xl border-[#E8E5DF] pl-9" data-testid="pm-booking-search" />
      </div>
      <div className="max-h-[52vh] overflow-y-auto space-y-2 pt-1">
        {filtered.map((b) => (
          <button key={b.booking_id} type="button" onClick={() => pickBooking(b)} className="w-full text-left bg-white border border-[#E8E5DF] rounded-xl p-3 hover:border-[#FF3B6B] hover:bg-[#FFFDF8] transition" data-testid={`pm-pick-${b.booking_id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="font-semibold text-sm text-[#1E1E24] truncate">
                  {b.child_name} <span className="text-[#6B6B7A] font-normal">· {b.date}{b.time_slot ? ` at ${b.time_slot}` : ''}</span>
                </p>
                <p className="text-xs text-[#6B6B7A] truncate">{b.customer_name || b.customer_email || b.booking_id}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-[10px] text-[#6B6B7A]">Outstanding</p>
                <p className="text-sm font-bold text-[#FF3B6B]">${outstandingOf(b).toFixed(2)}</p>
              </div>
            </div>
          </button>
        ))}
        {filtered.length === 0 && (<p className="text-center text-sm text-[#6B6B7A] py-6" data-testid="pm-booking-empty">No matching bookings.</p>)}
      </div>
    </div>
  );

  // ── Step: amount / note / pin / receipt ────────────────────────────────────
  const renderDetails = () => (
    <div className="space-y-4" data-testid="pm-step-details">
      {posMode && renderCustomerPicker()}
      {selectedBooking && (
        <div className="flex items-center justify-between bg-[#FFFDF8] border border-[#E8E5DF] rounded-xl p-3">
          <div className="min-w-0">
            <p className="text-xs text-[#6B6B7A]">Booking</p>
            <p className="text-sm font-semibold text-[#1E1E24] truncate">{selectedBooking.child_name} · {selectedBooking.date}</p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-xs text-[#6B6B7A]">Outstanding</p>
            <p className="text-base font-bold text-[#FF3B6B]" data-testid="pm-outstanding">${outstanding.toFixed(2)}</p>
          </div>
        </div>
      )}

      <div>
        <Label className="text-xs">{amountLocked ? (paymentType === 'deposit' ? 'Deposit due (CAD)' : 'Balance due (CAD)') : 'Amount to charge (CAD)'}</Label>
        {amountLocked ? (
          <div className="rounded-xl border border-[#E8E5DF] bg-[#FFFDF8] mt-1 px-4 py-3 flex items-center justify-between" data-testid="pm-amount-fixed">
            <span className="text-2xl font-bold text-[#1E1E24] tabular-nums">${amtNum.toFixed(2)}</span>
            <span className="text-[11px] text-[#6B6B7A]">{paymentType === 'balance' ? 'pays your booking in full' : '50% deposit'}</span>
          </div>
        ) : (
          <>
            <Input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-xl border-[#E8E5DF] mt-1 text-lg font-semibold" data-testid="pm-amount-input" />
            {!amountValid && amount !== '' && (<p className="text-[11px] text-[#E5484D] mt-1" data-testid="pm-amount-error">Amount must be greater than $0.00</p>)}
            {overpay && (
              <div className="mt-2 flex items-start gap-2 text-[11px] text-[#8A5A1F] bg-[#FFF7EB] border border-[#F5C15A] rounded-lg p-2" data-testid="pm-overpay-warning">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>This exceeds the outstanding balance of ${outstanding.toFixed(2)}. The overpayment will be recorded as a credit.</span>
              </div>
            )}
          </>
        )}
      </div>

      {posMode && !amountLocked && (
        <div className="rounded-xl border border-[#E8E5DF] bg-white p-3" data-testid="pm-hst-block">
          <div className="flex items-center justify-between">
            <div className="pr-3">
              <Label className="text-sm font-semibold text-[#1E1E24]">Add HST (13%)</Label>
              <p className="text-[11px] text-[#6B6B7A]">Off for HST-free sales (gift cards, non-taxable items). On adds 13% on top.</p>
            </div>
            <Switch checked={addHst} onCheckedChange={setAddHst} data-testid="pm-hst-toggle" />
          </div>
          {addHst && (
            <div className="mt-3 space-y-1 text-sm border-t border-[#F0EDE6] pt-2">
              <div className="flex justify-between text-[#6B6B7A]"><span>Subtotal</span><span className="tabular-nums" data-testid="pm-hst-subtotal">${(Number(amount) || 0).toFixed(2)}</span></div>
              <div className="flex justify-between text-[#6B6B7A]"><span>HST (13%)</span><span className="tabular-nums" data-testid="pm-hst-amount">${hstAmount.toFixed(2)}</span></div>
              <div className="flex justify-between font-bold text-[#1E1E24]"><span>Total to charge</span><span className="tabular-nums" data-testid="pm-hst-total">${totalCharge.toFixed(2)}</span></div>
            </div>
          )}
        </div>
      )}

      {isAdmin && (
        <div>
          <Label className="text-xs">Note (optional, appears on the receipt)</Label>
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Extra craft kit / phoned card payment" className="rounded-xl border-[#E8E5DF] mt-1" data-testid="pm-note-input" />
        </div>
      )}

      {posMode && renderReceiptControls()}

      {isAdmin && (
        <div>
          <Label className="text-xs flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Your 4-digit staff PIN</Label>
          <Input type="password" inputMode="numeric" maxLength={4} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="••••" className="rounded-xl border-[#E8E5DF] mt-1 text-center text-2xl tracking-[0.5em] font-bold" data-testid="pm-pin-input" />
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        {bookingList.length > 1 && (
          <Button variant="ghost" size="sm" onClick={() => setStep('booking')} className="text-xs" data-testid="pm-change-booking">
            <ArrowLeft className="w-3 h-3 mr-1" /> Change booking
          </Button>
        )}
        <Button onClick={() => setStep(posMode ? 'method' : 'card')} disabled={!amountValid || !pinValid} className="flex-1 bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="pm-continue-btn">
          <CreditCard className="w-4 h-4 mr-2" /> {posMode ? 'Choose payment method' : 'Continue to card'}
        </Button>
      </div>
    </div>
  );

  // ── Step: method picker (POS) ──────────────────────────────────────────────
  const renderMethodPick = () => {
    const methods = [
      { id: 'card', label: 'Credit / Debit Card', icon: CreditCard, color: '#FF3B6B', sub: 'Key in the card — no redirect', enabled: stripeConfig?.configured !== false, testId: 'pm-method-card' },
      { id: 'cash', label: 'Cash', icon: BanknoteIcon, color: '#FFD166', sub: 'Record cash received', enabled: true, testId: 'pm-method-cash' },
      { id: 'paypal', label: 'PayPal', icon: Smartphone, color: '#0070BA', sub: paypalConfig?.configured ? 'PayPal checkout' : 'PayPal not configured', enabled: !!paypalConfig?.configured, testId: 'pm-method-paypal' },
      { id: 'etransfer', label: 'E-Transfer', icon: Wallet, color: '#06D6A0', sub: 'Interac e-Transfer (pending)', enabled: true, testId: 'pm-method-etransfer' },
      { id: 'terminal', label: 'Tap Reader', icon: Radio, color: '#7C4DFF', sub: 'Stripe Terminal', enabled: stripeConfig?.configured !== false, testId: 'pm-method-terminal' },
    ];
    return (
      <div className="space-y-3" data-testid="pm-step-method">
        <div className="flex items-center justify-between bg-[#FFFDF8] border border-[#E8E5DF] rounded-xl p-3">
          <div className="min-w-0">
            <p className="text-xs text-[#6B6B7A]">Charging</p>
            <p className="text-sm font-semibold text-[#1E1E24] truncate">{effCustomer?.name || 'Walk-in customer'}</p>
          </div>
          <p className="text-lg font-bold text-[#1E1E24]">${totalCharge.toFixed(2)}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {methods.map((m) => (
            <button key={m.id} type="button" disabled={!m.enabled} onClick={() => { setCardStatus(null); if (m.id === 'cash') setCashReceived(totalCharge.toFixed(2)); setStep(m.id); }} data-testid={m.testId}
              className={`text-left bg-white border-2 rounded-xl p-3 transition flex items-center gap-3 ${m.enabled ? 'border-[#E8E5DF] hover:border-[#FF3B6B] hover:shadow-sm' : 'border-[#F0EDE6] opacity-50 cursor-not-allowed'}`}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${m.color}20` }}>
                <m.icon className="w-4 h-4" style={{ color: m.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm text-[#1E1E24]">{m.label}</p>
                <p className="text-[11px] text-[#6B6B7A] truncate">{m.sub}</p>
              </div>
            </button>
          ))}
        </div>
        <Button variant="ghost" size="sm" onClick={() => setStep('details')} className="text-xs" data-testid="pm-method-back">
          <ArrowLeft className="w-3 h-3 mr-1" /> Back
        </Button>
      </div>
    );
  };

  // ── Step: Stripe card (Elements) ───────────────────────────────────────────
  const renderCard = () => (
    <div className="space-y-3" data-testid="pm-step-card">
      <div className="flex items-center justify-between bg-[#FFFDF8] border border-[#E8E5DF] rounded-xl p-3">
        <span className="text-sm text-[#6B6B7A]">Charging</span>
        <span className="text-lg font-bold text-[#1E1E24]">${totalCharge.toFixed(2)} CAD</span>
      </div>
      <StripeCardForm
        context={isAdmin ? 'admin' : 'customer'}
        bookingId={selectedBooking?.booking_id || null}
        amount={totalCharge}
        note={note}
        pin={pin}
        paymentType={paymentType}
        customerEmail={effCustomer?.email || null}
        customerName={effCustomer?.name || null}
        customerPhone={effCustomer?.phone || null}
        customerId={effCustomer?.customer_id || null}
        isOneTime={effectiveOneTime}
        sendEmail={sendEmail && canEmailReceipt}
        sendSms={sendSms && canSmsReceipt}
        onCancel={() => setStep(posMode ? 'method' : 'details')}
        onSuccess={(result) => finishPos(result)}
      />
    </div>
  );

  // ── Step: Cash (POS) ───────────────────────────────────────────────────────
  const renderCash = () => {
    const cashNum = Number(cashReceived);
    const hasCash = cashReceived !== '' && !Number.isNaN(cashNum);
    const shortfall = hasCash && cashNum + 0.001 < totalCharge;
    const changeDue = hasCash && cashNum >= totalCharge ? cashNum - totalCharge : 0;
    return (
      <div className="space-y-3" data-testid="pm-step-cash">
        <Button variant="ghost" size="sm" onClick={() => setStep('method')} className="-ml-2 text-xs"><ArrowLeft className="w-3 h-3 mr-1" /> Back</Button>
        <div className="bg-[#FFFDF8] border-2 border-[#FFD166]/40 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BanknoteIcon className="w-8 h-8 text-[#FFD166]" />
            <div><p className="font-semibold text-[#1E1E24]">Record cash payment</p><p className="text-xs text-[#6B6B7A]">{addHst ? 'Total charged (incl. 13% HST)' : 'Amount charged'}</p></div>
          </div>
          <p className="text-xl font-bold text-[#1E1E24] tabular-nums" data-testid="pm-cash-amount-charged">${totalCharge.toFixed(2)}</p>
        </div>
        <div>
          <Label className="text-xs">Cash received from customer (CAD)</Label>
          <Input type="number" step="0.01" min="0" value={cashReceived} onChange={(e) => setCashReceived(e.target.value)} placeholder={totalCharge.toFixed(2)} className="rounded-xl border-[#E8E5DF] mt-1 text-lg font-semibold" data-testid="pm-cash-received-input" />
          {shortfall && (<p className="text-[11px] text-[#E5484D] mt-1" data-testid="pm-cash-shortfall-error">Cash received is less than the ${totalCharge.toFixed(2)} charged. Go back and lower the amount to charge for a partial payment.</p>)}
        </div>
        <div className="flex items-center justify-between rounded-xl border border-[#E8E5DF] bg-white px-4 py-3" data-testid="pm-cash-change-row">
          <span className="text-sm font-medium text-[#6B6B7A]">Change due to customer</span>
          <span className={`text-xl font-bold tabular-nums ${changeDue > 0 ? 'text-[#06D6A0]' : 'text-[#1E1E24]'}`} data-testid="pm-cash-change-due">${changeDue.toFixed(2)}</span>
        </div>
        <Button
          onClick={async () => {
            if (shortfall) { toast.error('Cash received is less than the amount charged'); return; }
            setBusy(true);
            try {
              const r = await axios.post(`${API_URL}/api/admin/manual-payments`, { amount: amtNum, payment_method: 'cash', pin, add_hst: addHst, cash_received: hasCash ? cashNum : totalCharge, ...commonPosPayload() }, { withCredentials: true });
              finishPos(r.data);
              toast.success(`$${totalCharge.toFixed(2)} cash recorded`);
            } catch (e) { toast.error(e.response?.data?.detail || 'Cash entry failed'); }
            finally { setBusy(false); }
          }}
          disabled={busy || !pinValid || shortfall}
          className="w-full bg-[#FFD166] hover:bg-[#F0BC50] text-[#1E1E24] rounded-full font-bold" data-testid="pm-cash-record-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BanknoteIcon className="w-4 h-4 mr-2" />} Record cash payment
        </Button>
      </div>
    );
  };

  // ── Step: PayPal (POS) ─────────────────────────────────────────────────────
  const renderPaypal = () => (
    <div className="space-y-3" data-testid="pm-step-paypal">
      <Button variant="ghost" size="sm" onClick={() => setStep('method')} className="-ml-2 text-xs"><ArrowLeft className="w-3 h-3 mr-1" /> Back</Button>
      <div className="bg-[#FFFDF8] border-2 border-[#0070BA]/30 rounded-xl p-4 flex items-center gap-3">
        <Smartphone className="w-8 h-8 text-[#0070BA]" />
        <div><p className="font-semibold text-[#1E1E24]">Pay ${totalCharge.toFixed(2)} via PayPal</p><p className="text-xs text-[#6B6B7A]">Complete the PayPal checkout below.</p></div>
      </div>
      {paypalConfig?.client_id ? (
        <PayPalScriptProvider options={{ 'client-id': paypalConfig.client_id, currency: 'CAD', intent: 'capture' }}>
          <PayPalButtons
            style={{ layout: 'vertical', shape: 'pill', color: 'gold' }}
            forceReRender={[totalCharge, effCustomer?.email]}
            createOrder={async () => {
              const r = await axios.post(`${API_URL}/api/payments/paypal/create-order`, { amount: totalCharge, ...commonPosPayload() }, { withCredentials: true });
              return r.data.order_id || r.data.id;
            }}
            onApprove={async (data) => {
              try {
                const r = await axios.post(`${API_URL}/api/payments/paypal/capture`, { order_id: data.orderID }, { withCredentials: true });
                finishPos(r.data);
                toast.success(`$${totalCharge.toFixed(2)} received via PayPal`);
              } catch (e) { toast.error(e.response?.data?.detail || 'PayPal capture failed'); }
            }}
            onError={() => toast.error('PayPal payment could not be completed')}
          />
        </PayPalScriptProvider>
      ) : (
        <p className="text-[11px] text-[#6B6B7A] text-center">PayPal is not configured.</p>
      )}
    </div>
  );

  // ── Step: E-Transfer (POS) ─────────────────────────────────────────────────
  const renderEtransfer = () => (
    <div className="space-y-3" data-testid="pm-step-etransfer">
      <Button variant="ghost" size="sm" onClick={() => setStep('method')} className="-ml-2 text-xs"><ArrowLeft className="w-3 h-3 mr-1" /> Back</Button>
      <div className="bg-[#FFFDF8] border-2 border-[#06D6A0]/40 rounded-xl p-4">
        <p className="font-semibold text-[#1E1E24]">Interac e-Transfer</p>
        <p className="text-xs text-[#6B6B7A] mt-1">Send to <strong>payments@creativebirthdays.ca</strong> — auto-deposit enabled.</p>
      </div>
      <div className="bg-[#FFF7EB] border border-[#F5C15A] rounded-xl p-3 text-[11px] text-[#8A5A1F] flex gap-2">
        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>Logged as <strong>pending</strong> until finance confirms the funds cleared. Receipt is sent once confirmed.</span>
      </div>
      <Button
        onClick={async () => {
          setBusy(true);
          try {
            const r = await axios.post(`${API_URL}/api/admin/payments/etransfer/initiate`, { amount: amtNum, pin, add_hst: addHst, ...commonPosPayload() }, { withCredentials: true });
            finishPos({ ...r.data, pending: true });
            toast.success('Logged as pending — confirm once funds clear');
          } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
          finally { setBusy(false); }
        }}
        disabled={busy || !pinValid}
        className="w-full bg-[#06D6A0] hover:bg-[#05B88A] text-white rounded-full" data-testid="pm-etransfer-pending-btn">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wallet className="w-4 h-4 mr-2" />} Mark as pending
      </Button>
    </div>
  );

  // ── Step: Terminal (POS, tap reader) ───────────────────────────────────────
  const startTerminal = useCallback(async () => {
    setBusy(true);
    setCardStatus('loading_sdk');
    try {
      if (!window.StripeTerminal) {
        await new Promise((resolve, reject) => {
          const tag = document.createElement('script');
          tag.src = 'https://js.stripe.com/terminal/v1/';
          tag.onload = resolve;
          tag.onerror = () => reject(new Error('Failed to load Stripe Terminal SDK'));
          document.head.appendChild(tag);
        });
      }
      const term = window.StripeTerminal.create({
        onFetchConnectionToken: async () => {
          const r = await axios.post(`${API_URL}/api/admin/stripe-terminal/connection-token`, {}, { withCredentials: true });
          return r.data.secret;
        },
        onUnexpectedReaderDisconnect: () => { setCardStatus('disconnected'); toast.error('Reader disconnected'); },
      });
      setTerminal(term);
      setCardStatus('discovering');
      const discover = await term.discoverReaders(stripeConfig?.simulated ? { simulated: true } : { simulated: false });
      if (discover.error) throw new Error(discover.error.message);
      if (!discover.discoveredReaders.length) throw new Error('No readers found. Enable simulated mode or pair a reader.');
      setCardStatus('connecting');
      const conn = await term.connectReader(discover.discoveredReaders[0]);
      if (conn.error) throw new Error(conn.error.message);
      setReader(conn.reader);
      setCardStatus('creating_intent');
      const intentRes = await axios.post(`${API_URL}/api/admin/stripe-terminal/payment-intent`, { amount: totalCharge, pin, ...commonPosPayload() }, { withCredentials: true });
      const { client_secret, payment_intent_id } = intentRes.data;
      setCardStatus('collecting');
      const collect = await term.collectPaymentMethod(client_secret);
      if (collect.error) throw new Error(collect.error.message);
      setCardStatus('processing');
      const proc = await term.processPayment(collect.paymentIntent);
      if (proc.error) throw new Error(proc.error.message);
      setCardStatus('finalizing');
      const final = await axios.post(`${API_URL}/api/admin/stripe-terminal/finalize`, { payment_intent_id }, { withCredentials: true });
      finishPos(final.data);
      toast.success(`$${totalCharge.toFixed(2)} charged via card terminal`);
    } catch (e) {
      setCardStatus('error');
      toast.error(`Terminal: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  }, [amtNum, totalCharge, pin, stripeConfig, effCustomer, sendEmail, sendSms, note, effectiveOneTime]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderTerminal = () => {
    const stages = {
      loading_sdk: 'Loading reader SDK…', discovering: 'Looking for readers…', connecting: 'Connecting…',
      creating_intent: 'Creating payment…', collecting: 'Tap, insert or swipe card…', processing: 'Processing…',
      finalizing: 'Recording payment…', error: 'Something went wrong — try again.', disconnected: 'Reader disconnected',
    };
    return (
      <div className="space-y-3" data-testid="pm-step-terminal">
        <Button variant="ghost" size="sm" onClick={() => setStep('method')} className="-ml-2 text-xs"><ArrowLeft className="w-3 h-3 mr-1" /> Back</Button>
        <div className="bg-[#FFFDF8] border-2 border-[#7C4DFF]/30 rounded-xl p-4 flex items-center gap-3">
          <Radio className="w-8 h-8 text-[#7C4DFF]" />
          <div><p className="font-semibold text-[#1E1E24]">Charge ${totalCharge.toFixed(2)} via tap reader</p><p className="text-xs text-[#6B6B7A]">{stripeConfig?.simulated ? 'Simulated reader (dev mode)' : 'Reader collects the card'}</p></div>
        </div>
        {cardStatus && (
          <div className="bg-white border border-[#E8E5DF] rounded-xl p-3 flex items-center gap-3 text-sm">
            {!['error', 'disconnected'].includes(cardStatus) && <Loader2 className="w-4 h-4 animate-spin text-[#7C4DFF]" />}
            <span className="text-[#1E1E24]">{stages[cardStatus]}</span>
          </div>
        )}
        <Button onClick={startTerminal} disabled={busy || !pinValid} className="w-full bg-[#7C4DFF] hover:bg-[#6a3ee0] text-white rounded-full" data-testid="pm-terminal-charge-btn">
          <Radio className="w-4 h-4 mr-2" /> Charge with terminal
        </Button>
      </div>
    );
  };

  // ── Step: success ─────────────────────────────────────────────────────────
  const renderSuccess = () => {
    const paymentId = success?.payment_id;
    const sent = success?.receipt_sent || {};
    const isPending = success?.pending;
    return (
      <div className="text-center py-8 space-y-3" data-testid="pm-step-success">
        <div className="w-14 h-14 rounded-full bg-[#06D6A0]/15 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-8 h-8 text-[#06D6A0]" />
        </div>
        <h3 className="text-xl font-bold text-[#1E1E24]">
          {isPending ? 'Logged as pending' : `$${Number(success?.amount || amtNum).toFixed(2)} received`}
        </h3>
        {Number(success?.change_due) > 0 && (
          <p className="text-sm font-semibold text-[#06D6A0]" data-testid="pm-cash-change-success">
            Change due to customer: ${Number(success.change_due).toFixed(2)}
          </p>
        )}
        {success?.hst_applied && Number(success?.tax_amount) > 0 && (
          <p className="text-xs text-[#6B6B7A]" data-testid="pm-hst-success">
            Incl. HST ${Number(success.tax_amount).toFixed(2)} · subtotal ${Number(success.pre_tax_amount || 0).toFixed(2)}
          </p>
        )}
        {posMode && effCustomer?.name && (
          <p className="text-sm text-[#6B6B7A]" data-testid="pm-success-customer">Charged to <strong>{effCustomer.name}</strong>{effectiveOneTime ? ' · One-time' : ''}</p>
        )}
        <p className="text-sm text-[#6B6B7A]">
          {isPending
            ? 'Admin will confirm once the e-Transfer funds clear.'
            : success?.fully_paid
              ? 'This booking is now fully paid 🎉'
              : (success?.new_outstanding != null
                  ? `Payment recorded. Remaining balance: $${Number(success.new_outstanding).toFixed(2)}.`
                  : 'Payment recorded successfully.')}
        </p>
        {(sent.email || sent.sms) && (
          <p className="text-xs text-[#06D6A0]" data-testid="pm-receipt-sent">
            Receipt sent{sent.email ? ` · email ${sent.email}` : ''}{sent.sms ? ` · SMS ${sent.sms}` : ''}
          </p>
        )}
        <div className="flex items-center justify-center gap-2 pt-1">
          {posMode && paymentId && !isPending && (
            <Button variant="outline" onClick={() => window.open(`${API_URL}/api/admin/pos-charges/${paymentId}/receipt.pdf`, '_blank')} className="rounded-full border-[#E8E5DF]" data-testid="pm-download-receipt">
              <Download className="w-4 h-4 mr-1" /> Receipt PDF
            </Button>
          )}
          <Button onClick={close} className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="pm-done-btn">Done</Button>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : close())}>
      <DialogContent className="bg-white max-w-lg max-h-[92vh] overflow-y-auto" data-testid="payment-modal">
        <DialogHeader>
          <DialogTitle>{step === 'success' ? 'Payment complete' : (title || (posMode ? 'Take payment' : 'Make a payment'))}</DialogTitle>
          <DialogDescription>
            {step === 'booking' && 'Find the booking you want to pay for.'}
            {step === 'details' && (posMode ? 'Pick a customer, enter the amount, and choose whether to send a receipt.' : (isAdmin ? 'Enter the amount and authorize with your PIN.' : 'Enter the amount you would like to pay.'))}
            {step === 'method' && 'Choose how the customer is paying.'}
            {step === 'card' && 'Enter card details below — you stay on this page.'}
            {step === 'cash' && 'Record a cash payment.'}
            {step === 'paypal' && 'Complete the PayPal checkout.'}
            {step === 'etransfer' && 'Log an incoming Interac e-Transfer.'}
            {step === 'terminal' && 'Collect the card on the tap reader.'}
            {step === 'success' && 'Payment processed.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'booking' && renderBookingPick()}
        {step === 'details' && renderDetails()}
        {step === 'method' && renderMethodPick()}
        {step === 'card' && renderCard()}
        {step === 'cash' && renderCash()}
        {step === 'paypal' && renderPaypal()}
        {step === 'etransfer' && renderEtransfer()}
        {step === 'terminal' && renderTerminal()}
        {step === 'success' && renderSuccess()}
      </DialogContent>
    </Dialog>
  );
};

export default PaymentModal;
